<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';

import Icon from 'dashboard/components-next/icon/Icon.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Popover from 'dashboard/components-next/popover/Popover.vue';
import WhatsappTemplate from './WhatsappTemplate.vue';
import TemplateListItem from 'dashboard/components-next/whatsapp/TemplateListItem.vue';
import {
  sortTemplates,
  templateMatches,
} from 'dashboard/helper/segurocorTemplates';

const props = defineProps({
  inboxId: {
    type: Number,
    required: true,
  },
  contact: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['sendMessage']);

const { t } = useI18n();
const getFilteredWhatsAppTemplates = useMapGetter(
  'inboxes/getFilteredWhatsAppTemplates'
);

const searchQuery = ref('');
const selectedTemplate = ref(null);

const whatsAppTemplateMessages = computed(() => {
  return getFilteredWhatsAppTemplates.value(props.inboxId);
});

const filteredTemplates = computed(() =>
  sortTemplates(
    whatsAppTemplateMessages.value.filter(template =>
      templateMatches(template, searchQuery.value)
    )
  )
);

const handlePopoverShow = () => {
  searchQuery.value = '';
  selectedTemplate.value = null;
};

const handlePopoverHide = () => {
  selectedTemplate.value = null;
};

const handleTemplateClick = template => {
  selectedTemplate.value = template;
};

const handleBack = () => {
  selectedTemplate.value = null;
};

const handleSendMessage = (template, hide) => {
  emit('sendMessage', template);
  hide();
};
</script>

<template>
  <Popover
    align="start"
    disable-mobile-view
    @show="handlePopoverShow"
    @hide="handlePopoverHide"
  >
    <Button
      icon="i-ri-whatsapp-line"
      :label="t('COMPOSE_NEW_CONVERSATION.FORM.WHATSAPP_OPTIONS.LABEL')"
      color="slate"
      size="sm"
      :disabled="selectedTemplate"
      class="!text-xs font-medium"
    />
    <template #content="{ hide }">
      <div
        v-if="!selectedTemplate"
        class="flex flex-col gap-2 p-4 items-center w-[21.875rem]"
      >
        <div class="relative w-full">
          <Icon
            icon="i-lucide-search"
            class="absolute size-3.5 top-2 ltr:left-3 rtl:right-3"
          />
          <input
            v-model="searchQuery"
            type="search"
            :placeholder="
              t(
                'COMPOSE_NEW_CONVERSATION.FORM.WHATSAPP_OPTIONS.SEARCH_PLACEHOLDER'
              )
            "
            class="w-full h-8 py-2 ltr:pl-10 rtl:pr-10 ltr:pr-2 rtl:pl-2 text-sm reset-base outline-none border-none rounded-lg bg-n-alpha-black2 dark:bg-n-solid-1 text-n-slate-12"
          />
        </div>
        <div class="flex flex-col w-full max-h-80 overflow-y-auto">
          <div
            v-for="template in filteredTemplates"
            :key="template.id"
            class="p-2 w-full rounded-lg cursor-pointer dark:hover:bg-n-alpha-3 hover:bg-n-alpha-1"
            @click="handleTemplateClick(template)"
          >
            <TemplateListItem :template="template" />
          </div>
        </div>
        <template v-if="filteredTemplates.length === 0">
          <p class="pt-2 w-full text-sm text-n-slate-11">
            {{
              t('COMPOSE_NEW_CONVERSATION.FORM.WHATSAPP_OPTIONS.EMPTY_STATE')
            }}
          </p>
        </template>
      </div>
      <WhatsappTemplate
        v-else
        :template="selectedTemplate"
        :contact="contact"
        @send-message="payload => handleSendMessage(payload, hide)"
        @back="handleBack"
      />
    </template>
  </Popover>
</template>
